import React, { useEffect, useMemo, useState } from "react";

import type { FinCostCenter, FinCostCenterGroup } from "../api/finTypes";
import CostCenterSelect from "./CostCenterSelect";

type Props = {
  /** Prefixo dos ids dos campos (ex.: "en" → enGroup, enSub, enCc). */
  idPrefix: string;
  value: string;
  onChange: (costCenterId: string) => void;
  costCenters: FinCostCenter[];
  groups: FinCostCenterGroup[];
  disabled?: boolean;
  /** Centro gravado que não está mais na lista (inativo). */
  current?: { id: string; code: string; name: string } | null;
  /** Conteúdo abaixo do select de centro (ex.: "Ratear entre centros"). */
  footer?: React.ReactNode;
};

const NO_GROUP = "sem-grupo";
const DIRECT = "direto";

const byCode = (a: FinCostCenter, b: FinCostCenter) => a.code.localeCompare(b.code, "pt-BR", { numeric: true });

/**
 * Classificação do lançamento em cascata (17/10/2026, pedido do usuário): grupo →
 * subgrupo → centro de custo. O que se grava é o centro; grupo e subgrupo filtram a
 * lista e, na edição, vêm do grupo do centro gravado. Sem grupos cadastrados, fica
 * só o select de centro. Lista com um centro só já o escolhe.
 */
export default function CostCenterCascade({ idPrefix, value, onChange, costCenters, groups, disabled, current, footer }: Props) {
  const [groupSel, setGroupSel] = useState("");
  const [subSel, setSubSel] = useState("");

  const hasUngrouped = costCenters.some((c) => !c.group || !groups.some((g) => g.id === c.group?.id || g.children.some((s) => s.id === c.group?.id)));
  const root = groups.find((g) => g.id === groupSel) ?? null;
  const directOf = (groupId: string) => costCenters.filter((c) => c.group?.id === groupId);

  // Na edição (ou quando o centro muda por fora), grupo e subgrupo seguem o centro gravado.
  useEffect(() => {
    if (!value) return;
    const cc = costCenters.find((c) => c.id === value);
    if (!cc) return;
    const gid = cc.group?.id;
    const owner = gid ? groups.find((g) => g.id === gid) : undefined;
    const parent = gid ? groups.find((g) => g.children.some((s) => s.id === gid)) : undefined;
    if (owner) {
      setGroupSel(owner.id);
      setSubSel(owner.children.length ? DIRECT : "");
    } else if (parent) {
      setGroupSel(parent.id);
      setSubSel(gid!);
    } else {
      setGroupSel(NO_GROUP);
      setSubSel("");
    }
  }, [value, costCenters, groups]);

  const options = useMemo(() => {
    if (!groupSel) return costCenters;
    if (groupSel === NO_GROUP) {
      return costCenters.filter(
        (c) => !c.group || !groups.some((g) => g.id === c.group?.id || g.children.some((s) => s.id === c.group?.id))
      );
    }
    if (!root) return [];
    if (subSel === DIRECT || !root.children.length) return directOf(root.id);
    if (subSel) return costCenters.filter((c) => c.group?.id === subSel);
    // Grupo escolhido, subgrupo ainda não: todos os centros do grupo.
    const ids = new Set([root.id, ...root.children.map((s) => s.id)]);
    return costCenters.filter((c) => c.group && ids.has(c.group.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupSel, subSel, costCenters, groups, root]);

  // Lista com um centro só: já escolhe. Centro que saiu da lista: limpa.
  useEffect(() => {
    if (!groupSel) return;
    if (options.length === 1 && value !== options[0].id) onChange(options[0].id);
    else if (value && options.length > 1 && !options.some((c) => c.id === value)) onChange("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  if (!groups.length) {
    return (
      <div className="dpFormGroup">
        <label htmlFor={`${idPrefix}Cc`}>Centro de custo *</label>
        <CostCenterSelect
          id={`${idPrefix}Cc`}
          value={value}
          onChange={onChange}
          costCenters={costCenters}
          groups={groups}
          current={current}
          disabled={disabled}
        />
        {footer}
      </div>
    );
  }

  const changeGroup = (next: string) => {
    setGroupSel(next);
    setSubSel("");
  };

  const sorted = options.slice().sort(byCode);
  const missing = current && value === current.id && !costCenters.some((c) => c.id === current.id) ? current : null;

  return (
    <>
      <div className="dpFormGroup">
        <label htmlFor={`${idPrefix}Group`}>Grupo *</label>
        <select id={`${idPrefix}Group`} className="dpSelect" value={groupSel} onChange={(e) => changeGroup(e.target.value)} disabled={disabled}>
          <option value="">Escolha</option>
          {groups.map((g) => (
            <option key={g.id} value={g.id}>
              {g.code} — {g.name}
            </option>
          ))}
          {hasUngrouped ? <option value={NO_GROUP}>Sem grupo</option> : null}
        </select>
      </div>

      {root && root.children.length > 0 ? (
        <div className="dpFormGroup">
          <label htmlFor={`${idPrefix}Sub`}>Subgrupo *</label>
          <select id={`${idPrefix}Sub`} className="dpSelect" value={subSel} onChange={(e) => setSubSel(e.target.value)} disabled={disabled}>
            <option value="">Escolha</option>
            {directOf(root.id).length ? <option value={DIRECT}>Direto em {root.code} — {root.name}</option> : null}
            {root.children.map((s) => (
              <option key={s.id} value={s.id}>
                {s.code} — {s.name}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      <div className="dpFormGroup">
        <label htmlFor={`${idPrefix}Cc`}>Centro de custo *</label>
        <select
          id={`${idPrefix}Cc`}
          className="dpSelect"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled || (!!groupSel && sorted.length === 0 && !missing)}
        >
          <option value="">{groupSel && sorted.length === 0 ? "Nenhum centro neste grupo" : "Escolha"}</option>
          {missing ? (
            <option value={missing.id}>
              {missing.code} — {missing.name} (inativo)
            </option>
          ) : null}
          {sorted.map((c) => (
            <option key={c.id} value={c.id}>
              {c.code} — {c.name}
            </option>
          ))}
        </select>
        {footer}
      </div>
    </>
  );
}
